import { UpperCasePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, skip, startWith, switchMap, tap } from 'rxjs';
import { StakeholderGroup } from '../../../models/stakeholder-group';
import { TopicOfInterest } from '../../../models/topic-of-interest';
import { UrlMakerPipe } from '../../../pipes/url-maker.pipe';
import { ContactService, ContactSimilarityRecord } from '../../../services/contact.service';
import { ContactImportItem, ContactImportRequest, ImportService } from '../../../services/import.service';
import { OrganisationService, OrganisationSimilarityRecord } from '../../../services/organisation.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { haveSameIds } from '../../../utils/utils';
import { ImportContactConfirmComponent, ImportContactConfirmData } from '../../dialogs/import-contact-confirm/import-contact-confirm.component';
import { AdvancedChipSelectComponent } from '../../misc/advanced-chip-select/advanced-chip-select.component';
import { Option } from '../../misc/nested-options';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';


@Component({
  selector: 'app-contact-import',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatChipsModule,
    AdvancedChipSelectComponent,
    MatButtonModule,
    MatRadioModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    UpperCasePipe,
    UrlMakerPipe
  ],
  templateUrl: './contact-import.component.html',
  styleUrl: './contact-import.component.scss',
})
export class ContactImportComponent {
  readonly importService = inject(ImportService);
  readonly refDataService = inject(ReferenceDataService);
  readonly contactService = inject(ContactService);
  readonly organisationService = inject(OrganisationService);
  readonly dialog = inject(MatDialog);
  readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => this.allTopics());
  readonly allStakeholderGroups = this.refDataService.getContactStakeholdeGroups();
  readonly allStakeholderGroupOptions = computed(() => this.allStakeholderGroups());

  batchId = input.required<number>();
  importItem = input.required<ContactImportItem>();
  item = computed(() => this.importItem().uploadItem);
  reloadRequested = output<void>();


  matchingContacts = signal<ContactSimilarityRecord[]>([]);
  matchingOrganisations = signal<OrganisationSimilarityRecord[]>([]);

  form = new FormGroup({
    contact: new FormGroup({
      firstName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      lastName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }),

    position: new FormControl<string>(''),
    organisation: new FormControl<string>(''),

    contactSelect: new FormControl<string>('new'),
    organisationSelect: new FormControl<string>('new'),

    topicsOfInterest: new FormControl<Option[]>([]),
    stakeholderGroups: new FormControl<Option[]>([], { nonNullable: true, validators: [Validators.required] }),
  });

  constructor() {
    effect(() => {
      const importItem = this.importItem();
      const item = importItem.uploadItem;

      console.debug('ContactImportComponent effect');

      // c'est chinois, mais je dois utiliser untracked pour ne pas que l'effect s'exécute
      // quand les champs advancedsselect (matchingContacts & matchingOrganisations) sont modifiés.
      // ce problème ne se produit qu'après la première sauvegarde parce que la souscription n'a
      // pas encore pu avoir lieu.
      untracked(() => {
        const matchingContacts = this.dedupeSimilarContacts(
          importItem.contactSimilarities.matchingItems,
          importItem.contactSimilarities.similarItems
        )
        this.matchingContacts.set(matchingContacts);
        const contactSelect = matchingContacts
          .find(v => ((v.nameSimilarity === 1) || (v.emailSimilarity === 1)))?.id?.toString() ?? 'new';



        const matchingOrganisations = this.dedupeSimilarOrganisations(
          importItem.organisationSimilarities.matchingItems,
          importItem.organisationSimilarities.similarItems
        );
        this.matchingOrganisations.set(matchingOrganisations);
        const organisationSelect = matchingOrganisations
          .find(v => v.nameSimilarity === 1)?.id?.toString() ?? 'new';


        this.form.reset({
          contact: {
            firstName: item.firstName ?? '',
            lastName: item.lastName ?? '',
            email: item.email ?? '',
          },
          position: item.position ?? '',
          organisation: item.organisation ?? '',
          contactSelect,
          organisationSelect,
          topicsOfInterest: this.topicsToOptions(importItem.validTopicsOfInterest),
          stakeholderGroups: this.groupsToOptions(importItem.validStakeholderGroups),
        }, { emitEvent: false });

      })
    });
  }

  ngOnInit() {
    // create subscriptions
    // Text controls
    this.form.controls.contact.valueChanges
      .pipe(
        // skip(1),
        debounceTime(500),
        map((value) => ({
          firstName: value.firstName!.trim(),
          lastName: value.lastName!.trim(),
          email: value.email!.trim(),
        })),
        tap(v => console.debug('Checking for similar contacts', v)),
        distinctUntilChanged((a, b) =>
          JSON.stringify(a) === JSON.stringify(b)
        ),
        filter(({ firstName, lastName, email }) =>
          !!(firstName || lastName || email)
        ),
        tap(v => console.debug('Checked for similar contacts', v)),
        switchMap(payload =>
          this.contactService.checkForSimilarContacts({ id: null, firstName: payload.firstName, lastName: payload.lastName, email: payload.email })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        console.debug('similar result', result);
        this.matchingContacts.set(
          this.dedupeSimilarContacts(
            result.matchingItems,
            result.similarItems
          )
        );
        const contactSelect =
          this.matchingContacts()
            .find(v => ((v.nameSimilarity === 1) || (v.emailSimilarity === 1)))?.id?.toString() ?? 'new';
        this.form.controls.contactSelect.patchValue(contactSelect, { emitEvent: false });

      });

    this.form.controls.organisation.valueChanges
      .pipe(
        // skip(1), // no need to check at first load
        debounceTime(500),
        map(value => (value ?? '').trim()),
        tap(v => console.debug('Checking for similar organisations', v)),
        distinctUntilChanged(),
        filter(value => !!value),
        tap(v => console.debug('Checked for similar organisations', v)),
        switchMap(name =>
          this.organisationService.checkForSimilarOrganisations({ id: null, name: name, acronym: name })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        console.debug('similar result', result);
        this.matchingOrganisations.set(
          this.dedupeSimilarOrganisations(
            result.matchingItems,
            result.similarItems
          )
        );
        const organisationSelect = this.matchingOrganisations()
          .find(v => v.nameSimilarity === 1)?.id?.toString() ?? 'new';

        this.form.controls.organisationSelect.patchValue(organisationSelect, { emitEvent: false });
      });

    // Select controls
    this.form.controls.contactSelect.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(v => {
        if (v === 'new') {
          const item = this.importItem().uploadItem;
          // emitEvent: false because there is no need to run similarity check when selecting new
          this.form.controls.contact.patchValue({
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
          }, { emitEvent: false });
          this.form.controls.topicsOfInterest.patchValue(this.topicsToOptions(this.importItem().validTopicsOfInterest));
          this.form.controls.stakeholderGroups.patchValue(this.groupsToOptions(this.importItem().validStakeholderGroups));
        } else {
          const contact = this.matchingContacts().find(c => c.id.toString() === v);
          if (!contact) {
            console.debug('no contact found for id ', v);
            return;
          }
          // emitEvent: false because there is no need to run similarity check when selecting an item
          this.form.controls.contact.patchValue({
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
          }, { emitEvent: false });
          // this.form.controls.topicsOfInterest.patchValue(this.topicsToOptions(contact.));
          // this.form.controls.stakeholderGroups.patchValue(this.groupsToOptions(this.importItem().validStakeholderGroups));
        }
      });

    this.form.controls.organisationSelect.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(v => {
        if (v === 'new') {
          // emitEvent: false because there is no need to run similarity check when selecting new
          this.form.controls.organisation.patchValue(this.importItem().uploadItem.organisation, { emitEvent: false });
        } else {
          const organisation = this.matchingOrganisations()
            .find(o => o.id.toString() === v);

          if (!organisation) {
            return;
          }
          // emitEvent: false because there is no need to run similarity check when selecting an item
          this.form.patchValue({ organisation: organisation.name ?? '', }, { emitEvent: false });
        }
      });
  }

  save() {
    console.log('saving');
    const originalData = this.item();
    const formData = this.form.getRawValue();

    const same = (a: string | null | undefined, b: string | null | undefined) =>
      (a ?? '') === (b ?? '');

    const referenceContact =
      formData.contactSelect === 'new'
        ? originalData
        : this.matchingContacts()
          .find(c => c.id.toString() === formData.contactSelect);

    const referenceOrganisationName =
      formData.organisationSelect === 'new'
        ? originalData.organisation ?? ''
        : this.matchingOrganisations()
          .find(o => o.id.toString() === formData.organisationSelect)
          ?.name ?? '';

    const data: ImportContactConfirmData = {
      newContact: formData.contactSelect === 'new',
      newOrganisation: formData.organisationSelect === 'new',
      originalData: {
        firstName: originalData.firstName,
        lastName: originalData.lastName,
        email: originalData.email,
        position: originalData.position ?? '',
        organisation: originalData.organisation ?? '',
        topicsOfInterest: this.importItem().validTopicsOfInterest ?? [],
        stakeholderGroups: this.importItem().validStakeholderGroups ?? [],
      },
      importData: {
        firstName: formData.contact.firstName,
        lastName: formData.contact.lastName,
        email: formData.contact.email,
        position: formData.position ?? '',
        organisation: formData.organisation ?? '',
        topicsOfInterest: formData.topicsOfInterest ?? [],
        stakeholderGroups: formData.stakeholderGroups ?? [],
      },
      // if a new contact is to be created, signal if uploaded data has been modified (original data <-> form data)
      // if a contact is to be updated, signal if db data has been modified (database data <-> form data)
      firstNameModified: referenceContact
        ? !same(referenceContact.firstName, formData.contact.firstName)
        : false,
      lastNameModified: referenceContact
        ? !same(referenceContact.lastName, formData.contact.lastName)
        : false,
      emailModified: referenceContact
        ? !same(referenceContact.email, formData.contact.email)
        : false,
      positionModified: !same(originalData.position, formData.position),
      organisationModified: !same(referenceOrganisationName, formData.organisation),
      topicsOfInterestModified: !haveSameIds(this.importItem().validTopicsOfInterest, formData.topicsOfInterest),
      stakeholderGroupsModified: !haveSameIds(this.importItem().validStakeholderGroups, formData.stakeholderGroups),
    }

    const dialogRef = this.dialog.open(ImportContactConfirmComponent, {
      width: '800px',
      minWidth: '400px',
      maxWidth: '95vw',
      height: '700px',
      maxHeight: '95vh',
      disableClose: true,
      data: data
    });
    dialogRef.afterClosed().subscribe(result => {
      console.debug("Dialog returned ", result);
      console.debug("formdata", this.form.getRawValue())
      if (result) {
        var data: ContactImportRequest = {
          contactId: (formData.contactSelect === 'new' ? null : Number(formData.contactSelect)),
          firstName: formData.contact.firstName,
          lastName: formData.contact.lastName,
          email: formData.contact.email,
          position: formData.position ?? '',
          organisationId: (formData.organisationSelect === 'new' ? null : Number(formData.organisationSelect)),
          organisationName: formData.organisation ?? '',
          isOrganisationName: true,
          topicsOfInterest: formData.topicsOfInterest!.map(v => ({ id: v.id, name: v.name })),
          stakeholderGroups: formData.stakeholderGroups!.map(v => ({ id: v.id, name: v.name, category: null }))
        }
        this.importService.importUploadItem(this.batchId(), this.item().id, data).subscribe({
          next: () => {
            this.snackBar.open('Item imported', 'Close', { duration: 3000 });
            this.reloadRequested.emit();
          },
          error: msg => {
            this.snackBar.open('Failed to import item', 'Close', { duration: 3000 });
            console.error('Import failed:', msg)
          }
        });
      }
    });
  }

  delete() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete row',
        message: 'Are you sure you want to delete this row?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.debug("Dialog returned ", result);
      if (result) {
        this.importService.deleteUploadItem(this.batchId(), this.item().id).subscribe({
          next: () => {
            this.snackBar.open('Row deleted', 'Close', { duration: 3000 });
            this.reloadRequested.emit();
          },
          error: msg => {
            this.snackBar.open('Failed to delete row', 'Close', { duration: 3000 });
            console.error('Delete failed:', msg)
          }
        });
      }
    });
  }

  refresh() {
    // const dialogRef = this.dialog.open(ConfirmDialogComponent, {
    //   data: {
    //     title: 'Reload data',
    //     message: 'Are you sure you want to reload this row? Edits will be lost.'
    //   }
    // });
    // dialogRef.afterClosed().subscribe(result => {
    //   console.debug("Dialog returned ", result);
    //   if (result) {
    this.form.patchValue({
      contact: {
        firstName: this.item().firstName,
        lastName: this.item().lastName,
        email: this.item().email,
      },
      position: this.item().position,
      organisation: this.item().organisation
    });
    this.form.get('topicsOfInterest')!.setValue(
      this.topicsToOptions(this.importItem().validTopicsOfInterest)
    );
    this.form.get('stakeholderGroups')!.setValue(
      this.groupsToOptions(this.importItem().validStakeholderGroups)
    );
    // No need to force a refresh of matchingContacts and matchingOrganisations
    // because it is automatically done in the valueChanges defined in ngOnInit
    //   }
    // });
  }

  dedupeSimilarOrganisations(list1: OrganisationSimilarityRecord[], list2: OrganisationSimilarityRecord[]): OrganisationSimilarityRecord[] {
    const dedupedMap1 = new Map(list1.map(item => [item.id, item]));
    const dedupedList1 = dedupedMap1.values();
    const dedupedList2 = list2.filter(item => !dedupedMap1.has(item.id));
    const sorted1 = [...dedupedList1].sort((a, b) => a.name!.localeCompare(b.name!));
    const sorted2 = [...dedupedList2].sort((a, b) => a.name!.localeCompare(b.name!));
    return [...sorted1, ...sorted2];
  }

  dedupeSimilarContacts(list1: ContactSimilarityRecord[], list2: ContactSimilarityRecord[]): ContactSimilarityRecord[] {
    const dedupedMap1 = new Map(list1.map(item => [item.id, item]));
    const dedupedList1 = dedupedMap1.values();
    const dedupedList2 = list2.filter(item => !dedupedMap1.has(item.id));
    const sorted1 = [...dedupedList1].sort((a, b) => {
      const last = a.lastName!.localeCompare(b.lastName!);
      return (last !== 0 ? last : a.firstName!.localeCompare(b.firstName!))
    });
    const sorted2 = [...dedupedList2].sort((a, b) => {
      const last = a.lastName!.localeCompare(b.lastName!);
      return (last !== 0 ? last : a.firstName!.localeCompare(b.firstName!))
    });
    return [...sorted1, ...sorted2];
  }

  topicsToOptions(topics: TopicOfInterest[]) {
    return topics.map(t => ({
      id: t.id,
      name: t.name
    }))
  }

  groupsToOptions(groups: StakeholderGroup[]) {
    return groups.map(t => ({
      id: t.id,
      name: t.name
    }))
  }
}
